// Store & actions
// Components
import Page from '../../../common-utils/components/structure/page/Page';
// Common
import RouteUrls from '@/app/root/enums/RouteUrls';
import HISTORICS_TABS from '../enums/tabs/HistoricsTabs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import { useEffect, useState } from 'react';
import useTabManager from '../hooks/useTabManager';
import Entity from '@/common-utils/components/renders/entity/Entity';
import PosTerminalsHistoricsTable from '@/app/modules/historics/components/tables/PosTerminalsHistoricsTable';
import SimCardsHistoricsTable from '@/app/modules/historics/components/tables/SimCardsHistoricsTable';
import UsersHistoricsTable from '@/app/modules/historics/components/tables/UsersHistoricsTable';
import historicsFields from '@/app/modules/historics/fields/historicsFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import useSessionStore from '@/app/store/session';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
/**
 * @returns {JSX} Historics
 */
function Historics() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = extractQueriesFromUrl(location.search);
    const urlParams = extractQueriesFromUrl(location.search);
    const email = urlParams[historicsFields.USERS_HISTORICS.EMAIL.key];
    const serialNumber = urlParams[historicsFields.POS_TERMINALS_HISTORICS.SERIAL_NUMBER.key];
    const simIccid = urlParams[historicsFields.SIM_CARDS_HISTORICS.SIM_ICCID.key];

    // Store
    const { permissions } = useSessionStore(s => s.session);

    // State
    const [activeTab, setActiveTab] = useState(Object.values(HISTORICS_TABS).find(tab => tab.name === view)?.name ?? HISTORICS_TABS.DEFAULT.name);
    const [customQuery, setCustomQuery] = useState({});
    const [showTableUsers, setShowTableUsers] = useState(false);
    const [showTablePosTerminals, setShowTablePosTerminals] = useState(false);
    const [showTableSimCards, setShowTableSimCards] = useState(false);

    // Hooks
    // Tab Manager
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: HISTORICS_TABS,
        navigate: navigate
    });

    useEffect(() => {
        if (!email) {
            setShowTableUsers(true);
        } else {
            setCustomQuery({
                filters: [
                    {
                        key: generateNestedFieldAccessor(
                            historicsFields.USERS_HISTORICS.EMAIL),
                        value: email
                    }
                ]
            });
        }
    }, [email]);

    useEffect(() => {
        if (!serialNumber) {
            setShowTablePosTerminals(true);
        } else {
            setCustomQuery({
                filters: [
                    {
                        key: generateNestedFieldAccessor(historicsFields.POS_TERMINALS_HISTORICS.SERIAL_NUMBER),
                        value: serialNumber
                    }
                ]
            });
        }
    }, [serialNumber]);

    useEffect(() => {
        if (!simIccid) {
            setShowTableSimCards(true);
        } else {
            setCustomQuery({
                filters: [
                    {
                        key: generateNestedFieldAccessor(
                            historicsFields.SIM_CARDS_HISTORICS.SIM_ICCID),
                        value: simIccid
                    }
                ]
            });
        }
    }, [simIccid]);

    useEffect(() => {
        if (activeTab !== view) {
            setCustomQuery({});
        }
    }, [activeTab]);

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.HISTORICS}
                    routeBase={RouteUrls.HISTORICS}
                >
                    <TabsList>
                        <TabsTrigger tab={HISTORICS_TABS.POS_TERMINALS} activeTab={activeTab} permissions={permissions}>
                            <Entity entity='page.historics.tabs.pos_terminals' />
                        </TabsTrigger>
                        <TabsTrigger tab={HISTORICS_TABS.SIM_CARDS} activeTab={activeTab} permissions={permissions}>
                            <Entity entity='page.historics.tabs.sim_cards' />
                        </TabsTrigger>
                        <TabsTrigger tab={HISTORICS_TABS.USERS} activeTab={activeTab} permissions={permissions}>
                            <Entity entity='page.historics.tabs.users' />
                        </TabsTrigger>
                    </TabsList>
                </Page.Header>
                <Page.Body data-cy='page-historics'>
                    <TabsContent tab={HISTORICS_TABS.POS_TERMINALS} permissions={permissions}>
                        {
                            showTablePosTerminals &&
                            <PosTerminalsHistoricsTable
                                customQuery={customQuery}
                                filtersEventName={setFiltersEventName('pos_terminals_historics')}
                            />
                        }
                    </TabsContent>
                    <TabsContent tab={HISTORICS_TABS.SIM_CARDS} permissions={permissions}>
                        {
                            showTableSimCards &&
                            <SimCardsHistoricsTable
                                customQuery={customQuery}
                                filtersEventName={setFiltersEventName('sim_cards_historics')}
                            />
                        }
                    </TabsContent>
                    <TabsContent tab={HISTORICS_TABS.USERS} permissions={permissions}>
                        {
                            showTableUsers &&
                            <UsersHistoricsTable
                                customQuery={customQuery}
                                filtersEventName={setFiltersEventName('users_historics')}
                            />
                        }
                    </TabsContent>
                </Page.Body>
            </Tabs>
        </Page>
    );
}

export default Historics;